import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/siteTranslations';

export default function Navbar() {
  const { lang, toggleLanguage } = useLanguage();
  const t = translations[lang].nav;
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [treatmentsOpen, setTreatmentsOpen] = useState(false);

  // Close menus on route change
  useEffect(() => {
    setMobileOpen(false);
    setTreatmentsOpen(false);
  }, [location.pathname]);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const treatmentsList = [
    { name: lang === 'da' ? 'Bindevævsmassage' : 'Deep Tissue Massage', slug: 'deep-tissue-massage' },
    { name: lang === 'da' ? 'Neuromuskulær massage' : 'Neuromuscular Massage', slug: 'neuromuscular-massage' },
    { name: lang === 'da' ? 'Biodynamisk Kranio-Sakral Terapi' : 'Bio Dynamic Craniosacral Therapy', slug: 'bio-dynamic-craniosacral-therapy' },
    { name: lang === 'da' ? 'Gravidmassage' : 'Pregnancy Massage', slug: 'pregnancy-massage' },
    { name: lang === 'da' ? 'Efterfødselsmassage' : 'Post Partum Massage', slug: 'post-partum-massage' },
    { name: lang === 'da' ? 'Sportsmassage' : 'Sports Massage', slug: 'sports-massage' },
    { name: lang === 'da' ? 'Velværemassage' : 'Wellness Massage', slug: 'wellness-massage' },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-[#E8E0D7]/60 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        
        {/* Logo & Brand */}
        <Link to="/" className="flex items-center gap-3.5 group">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=240,quality=90,format=auto/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/massage-with-jette-mwj-heart4c.jpg"
            alt="Massage with Jette Logo"
            className="h-12 sm:h-14 w-auto object-contain group-hover:scale-105 transition-transform"
          />
        </Link>

        {/* Desktop Navigation (>= 1200px) */}
        <nav className="hidden min-[1200px]:flex items-center gap-7">
          <Link
            to="/"
            className={`text-sm font-medium transition-colors ${
              isActive('/') && location.pathname === '/'
                ? 'text-[#2E3191] font-semibold border-b-2 border-[#2E3191] pb-1'
                : 'text-[#2C2825] hover:text-[#2E3191]'
            }`}
          >
            {t.home}
          </Link>

          <Link
            to="/om-jette"
            className={`text-sm font-medium transition-colors ${
              isActive('/om-jette')
                ? 'text-[#2E3191] font-semibold border-b-2 border-[#2E3191] pb-1'
                : 'text-[#2C2825] hover:text-[#2E3191]'
            }`}
          >
            {t.about}
          </Link>

          {/* Treatments Dropdown */}
          <div
            className="relative py-2"
            onMouseEnter={() => setTreatmentsOpen(true)}
            onMouseLeave={() => setTreatmentsOpen(false)}
          >
            <Link
              to="/behandlinger"
              className={`text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${
                isActive('/behandlinger')
                  ? 'text-[#2E3191] font-semibold border-b-2 border-[#2E3191] pb-1'
                  : 'text-[#2C2825] hover:text-[#2E3191]'
              }`}
            >
              {t.treatments}
              <i className={`fa-solid fa-chevron-down text-[10px] transition-transform ${treatmentsOpen ? 'rotate-180' : ''}`} />
            </Link>

            {treatmentsOpen && (
              <div className="absolute left-0 top-full pt-1 w-64 z-50 animate-in fade-in slide-in-from-top-1 duration-150">
                <div className="bg-white rounded-xl shadow-xl border border-[#E8E0D7] py-2 overflow-hidden">
                  <Link
                    to="/behandlinger"
                    className="block px-4 py-2 text-xs font-semibold uppercase tracking-wider text-[#2E3191] bg-[#F4EFEA]/50 hover:bg-[#F4EFEA]"
                  >
                    {translations[lang].treatmentsOverview.viewAll} →
                  </Link>
                  <div className="h-px bg-[#E8E0D7] my-1" />
                  {treatmentsList.map((item) => (
                    <Link
                      key={item.slug}
                      to={`/behandlinger/${item.slug}`}
                      className="block px-4 py-2.5 text-sm text-[#2C2825] hover:bg-[#F3EFEA] hover:text-[#2E3191] transition-colors"
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Link
            to="/priser"
            className={`text-sm font-medium transition-colors ${
              isActive('/priser')
                ? 'text-[#2E3191] font-semibold border-b-2 border-[#2E3191] pb-1'
                : 'text-[#2C2825] hover:text-[#2E3191]'
            }`}
          >
            {t.prices}
          </Link>

          <Link
            to="/galleri"
            className={`text-sm font-medium transition-colors ${
              isActive('/galleri')
                ? 'text-[#2E3191] font-semibold border-b-2 border-[#2E3191] pb-1'
                : 'text-[#2C2825] hover:text-[#2E3191]'
            }`}
          >
            {t.gallery}
          </Link>

          <Link
            to="/kontakt"
            className={`text-sm font-medium transition-colors ${
              isActive('/kontakt')
                ? 'text-[#2E3191] font-semibold border-b-2 border-[#2E3191] pb-1'
                : 'text-[#2C2825] hover:text-[#2E3191]'
            }`}
          >
            {t.contact}
          </Link>
        </nav>

        {/* Right Action Bar (Language + Book Button) */}
        <div className="hidden min-[1200px]:flex items-center gap-4">
          <button
            onClick={toggleLanguage}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#E8E0D7] bg-white text-xs font-semibold text-[#2C2825] hover:border-[#2E3191] hover:text-[#2E3191] transition-all cursor-pointer"
            title="Switch language / Skift sprog"
          >
            <i className="fa-solid fa-globe text-xs text-[#2E3191]" />
            <span className={lang === 'da' ? 'text-[#2E3191] font-bold' : 'text-[#6E6760]'}>DA</span>
            <span className="text-[#A39B92]">|</span>
            <span className={lang === 'en' ? 'text-[#2E3191] font-bold' : 'text-[#6E6760]'}>EN</span>
          </button>

          <a
            href="https://massage-with-jette.planway.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="px-5 py-2.5 rounded-full bg-[#2E3191] text-white text-sm font-semibold hover:bg-[#1E2063] shadow-xs hover:shadow-md transition-all active:scale-98 inline-flex items-center gap-1.5"
          >
            {t.bookNow}
            <i className="fa-solid fa-arrow-up-right-from-square text-xs opacity-80" />
          </a>
        </div>

        {/* Burger Button (< 1200px) */}
        <div className="flex min-[1200px]:hidden items-center gap-3">
          <button
            onClick={toggleLanguage}
            className="px-2.5 py-1 rounded-full border border-[#E8E0D7] bg-white text-xs font-bold text-[#2E3191]"
          >
            {lang.toUpperCase()}
          </button>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2.5 rounded-lg text-[#2C2825] hover:bg-[#F3EFEA] transition-colors focus:outline-none"
            aria-label="Toggle navigation menu"
          >
            <i className={`fa-solid ${mobileOpen ? 'fa-xmark' : 'fa-bars'} text-xl`} />
          </button>
        </div>
      </div>

      {/* Mobile Drawer Navigation (< 1200px) */}
      {mobileOpen && (
        <div className="min-[1200px]:hidden bg-[#FAF7F2] border-b border-[#E8E0D7] px-6 py-6 shadow-xl space-y-4 animate-in slide-in-from-top duration-200">
          <div className="flex flex-col space-y-3">
            <Link
              to="/"
              className={`text-base font-medium py-1.5 ${isActive('/') && location.pathname === '/' ? 'text-[#2E3191] font-bold' : 'text-[#2C2825]'}`}
            >
              {t.home}
            </Link>

            <Link
              to="/om-jette"
              className={`text-base font-medium py-1.5 ${isActive('/om-jette') ? 'text-[#2E3191] font-bold' : 'text-[#2C2825]'}`}
            >
              {t.about}
            </Link>

            <div className="space-y-1.5 pl-2 border-l-2 border-[#2E3191]/30 my-1">
              <Link
                to="/behandlinger"
                className={`text-base font-semibold py-1 block ${isActive('/behandlinger') ? 'text-[#2E3191]' : 'text-[#2C2825]'}`}
              >
                {t.treatments}
              </Link>
              {treatmentsList.map((item) => (
                <Link
                  key={item.slug}
                  to={`/behandlinger/${item.slug}`}
                  className="block text-sm text-[#6E6760] hover:text-[#2E3191] py-1 pl-2"
                >
                  {item.name}
                </Link>
              ))}
            </div>

            <Link
              to="/priser"
              className={`text-base font-medium py-1.5 ${isActive('/priser') ? 'text-[#2E3191] font-bold' : 'text-[#2C2825]'}`}
            >
              {t.prices}
            </Link>

            <Link
              to="/galleri"
              className={`text-base font-medium py-1.5 ${isActive('/galleri') ? 'text-[#2E3191] font-bold' : 'text-[#2C2825]'}`}
            >
              {t.gallery}
            </Link>

            <Link
              to="/kontakt"
              className={`text-base font-medium py-1.5 ${isActive('/kontakt') ? 'text-[#2E3191] font-bold' : 'text-[#2C2825]'}`}
            >
              {t.contact}
            </Link>

            <div className="pt-4 border-t border-[#E8E0D7] flex items-center justify-between">
              <span className="text-xs text-[#6E6760]">Sprog / Language:</span>
              <button
                onClick={toggleLanguage}
                className="px-4 py-1.5 rounded-full border border-[#2E3191] bg-white text-xs font-bold text-[#2E3191]"
              >
                {lang === 'da' ? 'Skift til English' : 'Switch to Dansk'}
              </button>
            </div>

            <a
              href="https://massage-with-jette.planway.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 block w-full text-center px-5 py-3 rounded-xl bg-[#2E3191] text-white text-base font-semibold shadow-sm hover:bg-[#1E2063] transition-colors"
            >
              {t.bookNow} <i className="fa-solid fa-arrow-up-right-from-square ml-1.5 text-xs opacity-80" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
